/**
 * @typedef {{isGated?: boolean}} Props
 * @typedef {import('../../../Blog/BlogSearchFormProps').BlogSearchFormValues} BlogSearchFormValues
 * @typedef {import('../../../shared/BlogService/BlogPostExcerpt').default} BlogPostExcerpt
 */

import { blogService } from '../../../service-container';
import { translate } from '../../Internationalization';
import BlogCard from './BlogCard';
import BlogCategoryFilterSlug from '../../../config/local/blogCategoryFilter';
import blogConfig from '../../../config/local/blog';
import BlogFilterSearch from '../../../Blog/BlogFilterSearch';
import {
	buildTagIdByName,
	fetchSiteSearchPage,
	mapSearchResultsToPostExcerpts,
	normalizePostExcerpt,
} from '../../../Blog/blogSearchUtils';
import images from '../../../config/local/images';
import Loading from '../../../shared/Loading';
import NoContent from '../../../shared/NoContent';
import Pagination from '../../../shared/Pagination';
import React from 'react';
import useGetAgentsPersonalData from '../../../Hooks/Agent/Getters/useGetAgentsPersonalData/useGetAgentsPersonalData';

const MAX_POSTS = 12;
const BLOG_ERROR_KEY = 'homeowner.pages.dashboard.marketplace.error';

/**
 * @typedef {{ categorizedPosts: BlogPostExcerpt[]; categoryTagFilters: {tagId: string; tagName: string; slug: string}[]; }} CategoryTagCacheValue
 */

/**
 * @param {BlogPostExcerpt} post
 * @returns {number[]}
 */
function getPostTagIds(post) {
	return post.tagIds ?? [];
}

/**
 * @param {number[]} postTagIds
 * @param {number} categoryNumber
 * @returns {boolean}
 */
function hasCategoryTag(postTagIds, categoryNumber) {
	return postTagIds.includes(categoryNumber);
}

/**
 * @param {number[]} postTagIds
 * @param {number | null} tagNumber
 * @returns {boolean}
 */
function hasOptionalTag(postTagIds, tagNumber) {
	if (tagNumber === null) {
		return true;
	}

	return postTagIds.includes(tagNumber);
}

/**
 * @param {BlogPostExcerpt} post
 * @param {string} normalizedSearch
 * @returns {boolean}
 */
function matchesLocalSearch(post, normalizedSearch) {
	if (!normalizedSearch) {
		return true;
	}

	const postTitle = (post.title ?? '').toLowerCase();
	const postContent = (post.content ?? '').toLowerCase();

	return (
		postTitle.includes(normalizedSearch) ||
		postContent.includes(normalizedSearch)
	);
}

/**
 * @param {BlogPostExcerpt} post
 * @param {number} categoryNumber
 * @param {number | null} tagNumber
 * @param {string} normalizedSearch
 * @returns {boolean}
 */
function matchesLocalTagAndSearch(
	post,
	categoryNumber,
	tagNumber,
	normalizedSearch
) {
	const postTagIds = getPostTagIds(post);

	if (!hasCategoryTag(postTagIds, categoryNumber)) {
		return false;
	}

	return (
		hasOptionalTag(postTagIds, tagNumber) &&
		matchesLocalSearch(post, normalizedSearch)
	);
}

/**
 * @param {string} currentSearchQuery
 * @param {string | undefined} currentCategoryId
 * @param {string | null} currentTagId
 * @param {BlogSearchFormValues} values
 * @returns {boolean}
 */
function hasUnchangedFilters(
	currentSearchQuery,
	currentCategoryId,
	currentTagId,
	values
) {
	const categoryId = values.categoryId ?? undefined;
	const searchQuery = values.searchQuery ?? '';
	const tagId = values.tagId ?? null;

	return (
		currentSearchQuery === searchQuery &&
		currentCategoryId === categoryId &&
		currentTagId === tagId
	);
}

/**
 * @param {string | undefined} currentCategoryId
 * @param {BlogSearchFormValues} values
 * @returns {boolean}
 */
function isCategoryCleared(currentCategoryId, values) {
	return (
		values.categoryId === undefined &&
		values.categoryId !== null &&
		currentCategoryId !== undefined
	);
}

/**
 * @param {{
 *  categoryId: string | undefined;
 *  tagId: string | null;
 *  selectedCategoryId: string | undefined;
 *  allCategorizedPostsCategoryId: string | undefined;
 *  allCategorizedPostsCount: number;
 * }} input
 * @returns {boolean}
 */
function canUseLocalFilter(input) {
	return (
		input.categoryId !== undefined &&
		input.tagId !== null &&
		input.selectedCategoryId === input.categoryId &&
		input.allCategorizedPostsCategoryId === input.categoryId &&
		input.allCategorizedPostsCount > 0
	);
}

/**
 * @param {BlogSearchFormValues} values
 * @returns {{ categoryId: string | undefined; searchQuery: string; tagId: string | null; }}
 */
function normalizeSearchValues(values) {
	return {
		categoryId: values.categoryId ?? undefined,
		searchQuery: values.searchQuery ?? '',
		tagId: values.tagId ?? null,
	};
}

/**
 * @param {boolean} isGettingData
 * @param {string} errorMessage
 * @param {number} postsCount
 * @returns {boolean}
 */
function shouldShowInitialLoading(isGettingData, errorMessage, postsCount) {
	return isGettingData && !errorMessage && postsCount === 0;
}

/**
 * @param {Props} props
 * @returns {JSX.Element}
 */
export default function BlogsEmbed(props) {
	const agent = useGetAgentsPersonalData();
	const postsPerPage = props.isGated ? 3 : MAX_POSTS;
	const categoryTagCacheRef = React.useRef(
		/** @type {Map<string, CategoryTagCacheValue>} */(new Map())
	);
	const siteSearchResultsCacheRef = React.useRef(
		/** @type {Map<string, BlogPostExcerpt[]>} */ (new Map())
	);
	const latestSelectedCategoryIdRef = React.useRef(
		/** @type {string | undefined} */(undefined)
	);
	const [allCategorizedPosts, setAllCategorizedPosts] = React.useState(
		/** @type {BlogPostExcerpt[]} */([])
	);
	const [allCategorizedPostsCategoryId, setAllCategorizedPostsCategoryId] =
		React.useState(/** @type {string | undefined} */(undefined));
	const [allTags, setAllTags] = React.useState(
		/** @type {{tagId: string; tagName: string; slug: string}[]} */([])
	);
	const [categoryFilters, setCategoryFilters] = React.useState(
		/** @type {{tagId: string; tagName: string; slug: string}[]} */([])
	);
	const [errorMessage, setErrorMessage] = React.useState('');
	const [isGettingData, setIsGettingData] = React.useState(true);
	const [currentPageNumber, setCurrentPageNumber] = React.useState(1);
	const [lastPageNumber, setLastPageNumber] = React.useState(1);
	const [postExcerptList, setPostExcerptList] = React.useState(
		/** @type {BlogPostExcerpt[]} */([])
	);
	const [searchQuery, setSearchQuery] = React.useState('');
	const [selectedCategoryId, setSelectedCategoryId] = React.useState(
		/** @type {string | undefined} */(undefined)
	);
	const [selectedTagId, setSelectedTagId] = React.useState(
		/** @type {string | null} */(null)
	);
	const [tagFilterList, setTagFilterList] = React.useState(
		/** @type {{tagId: string; tagName: string}[]} */([])
	);
	const tagIdByName = React.useMemo(
		() => buildTagIdByName(allTags),
		[allTags]
	);
	const siteSearchMapOptions = React.useMemo(
		() => ({
			defaultAuthorImage: blogConfig.defaultAuthorImage,
			defaultImageUrl: images.blog.default.medium,
			tagIdByName,
		}),
		[tagIdByName]
	);

	/**
	 * @param {BlogPostExcerpt[]} posts
	 * @returns {BlogPostExcerpt[]}
	 */
	function normalizePostExcerptList(posts) {
		return posts.map((post) => normalizePostExcerpt(post, tagIdByName));
	}

	/**
	 * @param {string} nextSearchQuery
	 * @param {number} pageNumber
	 * @returns {Promise<{ posts: BlogPostExcerpt[]; totalPages: number }>}
	 */
	async function getSiteSearchPostExcerpts(nextSearchQuery, pageNumber) {
		const body = await fetchSiteSearchPage({
			limit: postsPerPage,
			offset: (pageNumber - 1) * postsPerPage,
			query: nextSearchQuery,
			searchUrl: blogConfig.api.search,
		});
		const total = body.total ?? 0;

		return {
			posts: mapSearchResultsToPostExcerpts(
				body.results ?? [],
				siteSearchMapOptions
			),
			totalPages: total > 0 ? Math.ceil(total / postsPerPage) : 0,
		};
	}

	/**
	 * @param {string} nextSearchQuery
	 * @returns {Promise<BlogPostExcerpt[]>}
	 */
	async function getAllSiteSearchPostExcerpts(nextSearchQuery) {
		const normalizedSearchQuery = nextSearchQuery.trim().toLowerCase();
		const cachedPosts = siteSearchResultsCacheRef.current.get(
			normalizedSearchQuery
		);

		if (cachedPosts !== undefined) {
			return cachedPosts;
		}

		const posts = [];
		const searchPageLimit = 100;
		let offset = 0;
		let total = 0;

		do {
			const body = await fetchSiteSearchPage({
				limit: searchPageLimit,
				offset,
				query: nextSearchQuery,
				searchUrl: blogConfig.api.search,
			});
			total = body.total ?? 0;

			posts.push(
				...mapSearchResultsToPostExcerpts(
					body.results ?? [],
					siteSearchMapOptions
				)
			);
			offset += searchPageLimit;
		} while (offset < total);

		siteSearchResultsCacheRef.current.set(normalizedSearchQuery, posts);
		return posts;
	}

	React.useEffect(() => {
		void initializeData();
	}, []);

	React.useEffect(() => {
		latestSelectedCategoryIdRef.current = selectedCategoryId;
	}, [selectedCategoryId]);

	/**
	 * @param {() => Promise<void>} callback
	 * @returns {Promise<void>}
	 */
	async function runWithRequestState(callback) {
		setIsGettingData(true);

		try {
			await callback();
			setErrorMessage('');
		} catch (error) {
			setErrorMessage(translate(BLOG_ERROR_KEY));
		} finally {
			setIsGettingData(false);
		}
	}

	/** @returns {Promise<void>} */
	async function initializeData() {
		await runWithRequestState(async () => {
			const [postResponse, tagsResponse] = await Promise.all([
				blogService.getPostExcerpts('', 1, postsPerPage),
				blogService.getAllPostTags(),
			]);
			const tags = tagsResponse?.tags ?? [];
			const allowedSet = new Set(BlogCategoryFilterSlug);
			const matchedTags = tags.filter((tag) => allowedSet.has(tag.slug));

			setAllTags([...tags]);
			setCategoryFilters([...matchedTags]);
			setCurrentPageNumber(1);
			setLastPageNumber(postResponse.totalPages ?? 1);
			setPostExcerptList(
				normalizePostExcerptList(postResponse.posts ?? [])
			);
		});
	}

	/**
	 * @param {string} nextSearchQuery
	 * @param {number} pageNumber
	 * @param {string | undefined} categoryId
	 * @returns {Promise<{posts: BlogPostExcerpt[]; totalPages: number}>}
	 */
	async function fetchPostExcerpts(nextSearchQuery, pageNumber, categoryId) {
		const normalizedSearchQuery = nextSearchQuery.trim();
		if (normalizedSearchQuery !== '' && categoryId === undefined) {
			return getSiteSearchPostExcerpts(normalizedSearchQuery, pageNumber);
		}

		return blogService.getPostExcerpts(
			nextSearchQuery ?? '',
			pageNumber,
			postsPerPage,
			categoryId ?? undefined
		);
	}

	/**
	 * @param {string} nextSearchQuery
	 * @param {number} pageNumber
	 * @param {string | undefined} categoryId
	 * @param {BlogPostExcerpt[]} fetchedPosts
	 * @param {number | undefined} totalPages
	 * @returns {void}
	 */
	function applyFetchedPostExcerptsState(
		nextSearchQuery,
		pageNumber,
		categoryId,
		fetchedPosts,
		totalPages
	) {
		setCurrentPageNumber(pageNumber);
		setErrorMessage('');
		setLastPageNumber(totalPages ?? 1);
		setPostExcerptList(fetchedPosts);
		setSearchQuery(nextSearchQuery ?? '');
		setSelectedCategoryId(categoryId);
	}

	/**
	 * @param {string} nextSearchQuery
	 * @param {number} pageNumber
	 * @param {string | undefined} categoryId
	 * @returns {Promise<BlogPostExcerpt[]>}
	 */
	async function getAndSetPostExcerptList(
		nextSearchQuery,
		pageNumber,
		categoryId
	) {
		let fetchedPosts = /** @type {BlogPostExcerpt[]} */ ([]);

		await runWithRequestState(async () => {
			const result = await fetchPostExcerpts(
				nextSearchQuery,
				pageNumber,
				categoryId
			);
			fetchedPosts = normalizePostExcerptList(result.posts ?? []);
			applyFetchedPostExcerptsState(
				nextSearchQuery,
				pageNumber,
				categoryId,
				fetchedPosts,
				result.totalPages
			);
		});

		return fetchedPosts;
	}

	/**
	 * @param {string} nextSearchQuery
	 * @param {string} categoryId
	 * @param {string | null} tagId
	 * @param {number} pageNumber
	 * @param {BlogPostExcerpt[]} [categorizedPosts]
	 * @returns {void}
	 */
	function setLocalFilteredPosts(
		nextSearchQuery,
		categoryId,
		tagId,
		pageNumber,
		categorizedPosts
	) {
		const normalizedSearch = nextSearchQuery.trim().toLowerCase();
		const categoryNumber = Number(categoryId);
		const tagNumber = !tagId ? null : Number(tagId);
		const sourcePosts = categorizedPosts ?? allCategorizedPosts;
		const filteredPosts = sourcePosts.filter((post) =>
			matchesLocalTagAndSearch(
				post,
				categoryNumber,
				tagNumber,
				normalizedSearch
			)
		);
		const localLastPageNumber =
			filteredPosts.length > 0
				? Math.ceil(filteredPosts.length / postsPerPage)
				: 1;
		const localCurrentPage = Math.min(
			Math.max(pageNumber, 1),
			localLastPageNumber
		);
		const startIndex = (localCurrentPage - 1) * postsPerPage;
		const paginatedFilteredPosts = filteredPosts.slice(
			startIndex,
			startIndex + postsPerPage
		);

		setAllCategorizedPosts(sourcePosts);
		setAllCategorizedPostsCategoryId(categoryId);
		setCurrentPageNumber(localCurrentPage);
		setErrorMessage('');
		setLastPageNumber(localLastPageNumber);
		setPostExcerptList(paginatedFilteredPosts);
		setSearchQuery(nextSearchQuery);
		setSelectedCategoryId(categoryId);
		setSelectedTagId(tagId);
	}

	/**
	 * @param {BlogPostExcerpt[]} categorizedPosts
	 * @returns {Set<number>}
	 */
	function collectTagIds(categorizedPosts) {
		const tagIds = new Set();

		for (const post of categorizedPosts) {
			for (const id of post.tagIds ?? []) {
				tagIds.add(id);
			}
		}

		return tagIds;
	}

	/**
	 * @param {Set<number>} tagIds
	 * @returns {{tagId: string; tagName: string; slug: string}[]}
	 */
	function getFilteredTags(tagIds) {
		return allTags.filter((tag) => tagIds.has(Number(tag.tagId)));
	}

	function clearTagFilteringData() {
		setAllCategorizedPosts([]);
		setAllCategorizedPostsCategoryId(undefined);
		setTagFilterList([]);
	}

	/**
	 * @param {BlogPostExcerpt[]} categorizedPosts
	 * @returns {{tagId: string; tagName: string; slug: string}[]}
	 */
	function getTagFiltersFromCategorizedPosts(categorizedPosts) {
		const tagIds = collectTagIds(categorizedPosts);
		return getFilteredTags(tagIds);
	}

	/**
	 * @param {string} categoryId
	 * @returns {CategoryTagCacheValue | undefined}
	 */
	function getCachedCategoryTagData(categoryId) {
		return categoryTagCacheRef.current.get(categoryId);
	}

	/**
	 * @param {string} categoryId
	 * @param {BlogPostExcerpt[]} categorizedPosts
	 * @param {{tagId: string; tagName: string; slug: string}[]} categoryTagFilters
	 * @returns {void}
	 */
	function cacheCategoryTagData(
		categoryId,
		categorizedPosts,
		categoryTagFilters
	) {
		categoryTagCacheRef.current.set(categoryId, {
			categorizedPosts,
			categoryTagFilters,
		});
	}

	/**
	 * @param {string} categoryId
	 * @param {BlogPostExcerpt[]} categorizedPosts
	 * @returns {void}
	 */
	function setImmediateCategoryTags(categoryId, categorizedPosts) {
		if (!categorizedPosts.length) {
			return;
		}

		const categoryTagFilters =
			getTagFiltersFromCategorizedPosts(categorizedPosts);

		if (!categoryTagFilters.length) {
			return;
		}

		cacheCategoryTagData(categoryId, categorizedPosts, categoryTagFilters);
		setAllCategorizedPosts(categorizedPosts);
		setAllCategorizedPostsCategoryId(categoryId);
		setTagFilterList(categoryTagFilters);
	}

	/**
	 * @param {string} categoryId
	 * @returns {Promise<void>}
	 */
	async function loadAndSetCategoryTags(categoryId) {
		const cachedCategoryData = getCachedCategoryTagData(categoryId);

		if (cachedCategoryData) {
			if (latestSelectedCategoryIdRef.current !== categoryId) {
				return;
			}

			setAllCategorizedPosts(cachedCategoryData.categorizedPosts);
			setAllCategorizedPostsCategoryId(categoryId);
			setTagFilterList(cachedCategoryData.categoryTagFilters);
			return;
		}

		const data = await blogService.getAllTagIds(categoryId);
		const categorizedPosts = normalizePostExcerptList(data?.results ?? []);
		const categoryTagFilters =
			getTagFiltersFromCategorizedPosts(categorizedPosts);

		cacheCategoryTagData(categoryId, categorizedPosts, categoryTagFilters);

		if (latestSelectedCategoryIdRef.current !== categoryId) {
			return;
		}

		setAllCategorizedPosts(categorizedPosts);
		setAllCategorizedPostsCategoryId(categoryId);
		setTagFilterList(categoryTagFilters);
	}

	/**
	 * @param {string} nextSearchQuery
	 * @param {string | undefined} categoryId
	 * @param {string | null} tagId
	 * @returns {Promise<void>}
	 */
	async function setTagFilters(nextSearchQuery, categoryId, tagId) {
		if (categoryId === undefined) {
			clearTagFilteringData();
			await getAndSetPostExcerptList(nextSearchQuery, 1, categoryId);
			return;
		}

		if (nextSearchQuery.trim() !== '') {
			await runWithRequestState(async () => {
				const allSearchPosts = await getAllSiteSearchPostExcerpts(
					nextSearchQuery
				);
				const categoryNumber = Number(categoryId);
				const categoryFilteredPosts = allSearchPosts.filter((post) =>
					hasCategoryTag(getPostTagIds(post), categoryNumber)
				);
				const categoryTagFilters = getTagFiltersFromCategorizedPosts(
					categoryFilteredPosts
				);

				setAllCategorizedPosts(categoryFilteredPosts);
				setAllCategorizedPostsCategoryId(categoryId);
				setTagFilterList(categoryTagFilters);
				setLocalFilteredPosts(
					nextSearchQuery,
					categoryId,
					tagId,
					1,
					categoryFilteredPosts
				);
			});
			return;
		}

		const fetchedPosts = await getAndSetPostExcerptList(
			nextSearchQuery,
			1,
			categoryId
		);

		if (tagId === null) {
			setImmediateCategoryTags(categoryId, fetchedPosts);
			void loadAndSetCategoryTags(categoryId);
		}
	}

	/**
	 * @param {string} nextSearchQuery
	 * @returns {void}
	 */
	function resetFiltersForClearedCategory(nextSearchQuery) {
		clearTagFilteringData();
		setSearchQuery(nextSearchQuery);
		setSelectedCategoryId(undefined);
		setSelectedTagId(null);
	}

	/**
	 * @param {BlogSearchFormValues} values
	 * @returns {{ categoryId: string | undefined; searchQuery: string; tagId: string | null; }}
	 */
	function getSubmitSelection(values) {
		const normalizedValues = normalizeSearchValues(values);
		const isSearchCleared =
			searchQuery.trim() !== '' &&
			normalizedValues.searchQuery.trim() === '';

		return {
			...normalizedValues,
			tagId:
				isSearchCleared && normalizedValues.categoryId !== undefined
					? null
					: normalizedValues.tagId,
		};
	}

	/**
	 * @param {string | undefined} categoryId
	 * @param {string | null} tagId
	 * @returns {boolean}
	 */
	function canApplyImmediateLocalSelection(categoryId, tagId) {
		if (categoryId === undefined) {
			return false;
		}

		return canUseLocalFilter({
			allCategorizedPostsCategoryId,
			allCategorizedPostsCount: allCategorizedPosts.length,
			categoryId,
			selectedCategoryId,
			tagId,
		});
	}

	/**
	 * @param {string | undefined} categoryId
	 * @returns {void}
	 */
	function applyCachedTagFilters(categoryId) {
		if (categoryId === selectedCategoryId || categoryId === undefined) {
			return;
		}

		const cachedCategoryData = getCachedCategoryTagData(categoryId);
		if (!cachedCategoryData) {
			return;
		}

		setTagFilterList(cachedCategoryData.categoryTagFilters);
	}

	/**
	 * @param {BlogSearchFormValues} values
	 * @returns {Promise<void>}
	 */
	async function handleSearchFormSubmit(values) {
		if (
			hasUnchangedFilters(
				searchQuery,
				selectedCategoryId,
				selectedTagId,
				values
			)
		) {
			return;
		}

		const {
			categoryId,
			searchQuery: nextSearchQuery,
			tagId,
		} = getSubmitSelection(values);

		if (isCategoryCleared(selectedCategoryId, values)) {
			resetFiltersForClearedCategory(nextSearchQuery);
			await getAndSetPostExcerptList(nextSearchQuery, 1, undefined);
			return;
		}

		if (canApplyImmediateLocalSelection(categoryId, tagId)) {
			setLocalFilteredPosts(nextSearchQuery, categoryId, tagId, 1);
			return;
		}

		setSearchQuery(nextSearchQuery);
		setSelectedCategoryId(categoryId);
		setSelectedTagId(tagId);
		applyCachedTagFilters(categoryId);

		await setTagFilters(nextSearchQuery, categoryId, tagId);
	}

	/**
	 * @returns {boolean}
	 */
	function canPaginateCategorySearchLocally() {
		return (
			selectedCategoryId !== undefined &&
			selectedTagId === null &&
			searchQuery.trim() !== '' &&
			allCategorizedPostsCategoryId === selectedCategoryId &&
			allCategorizedPosts.length > 0
		);
	}

	/**
	 * @returns {boolean}
	 */
	function shouldPaginateLocally() {
		return (
			canUseLocalFilter({
				allCategorizedPostsCategoryId,
				allCategorizedPostsCount: allCategorizedPosts.length,
				categoryId: selectedCategoryId,
				selectedCategoryId,
				tagId: selectedTagId,
			}) || canPaginateCategorySearchLocally()
		);
	}

	/**
	 * @param {number} pageNumber
	 * @returns {Promise<void>}
	 */
	async function handlePageChange(pageNumber) {
		if (shouldPaginateLocally() && selectedCategoryId !== undefined) {
			setLocalFilteredPosts(
				searchQuery,
				selectedCategoryId,
				selectedTagId,
				pageNumber
			);
			return;
		}

		await getAndSetPostExcerptList(
			searchQuery,
			pageNumber,
			selectedCategoryId
		);
	}

	const isInitialLoading = shouldShowInitialLoading(
		isGettingData,
		errorMessage,
		postExcerptList.length
	);

	if (isInitialLoading) {
		return <Loading />;
	}

	function renderBlogResults() {
		if (isGettingData) {
			return <Loading />;
		}

		if (errorMessage) {
			return <NoContent message={errorMessage} />;
		}

		if (!postExcerptList.length) {
			return <NoContent />;
		}

		return (
			<React.Fragment>
				<section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
					{postExcerptList.map((postExcerpt) => (
						<BlogCard
							agentSlug={agent?.data?.slug}
							content={postExcerpt.content}
							date={postExcerpt.date}
							imageUrl={postExcerpt.imageUrl}
							isGated={props.isGated}
							key={postExcerpt.slug}
							slug={postExcerpt.slug}
							title={postExcerpt.title}
						/>
					))}
				</section>
				{!props.isGated && (
					<Pagination
						currentPageNumber={currentPageNumber}
						lastPageNumber={lastPageNumber}
						setCurrentPageNumber={handlePageChange}
					/>
				)}
			</React.Fragment>
		);
	}

	return (
		<React.Fragment>
			{!props.isGated && (
				<BlogFilterSearch
					categoryFilterList={categoryFilters}
					className="w-full mb-8"
					disabled={isGettingData}
					onSubmit={handleSearchFormSubmit}
					searchQuery={searchQuery}
					selectedCategoryId={selectedCategoryId}
					selectedTagId={selectedTagId}
					tagFilterList={tagFilterList}
				/>
			)}
			{props.isGated ? (
				renderBlogResults()
			) : (
				<section className="border-t border-grey-300 pt-8">
					{renderBlogResults()}
				</section>
			)}
		</React.Fragment>
	);
}
